import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { History, ChevronLeft, ChevronRight, FilterX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from './shared';

const colorForAction = (action) => {
  if (action.includes('delete')) return 'text-red-400';
  if (action.includes('create') || action.includes('invite.create')) return 'text-emerald-400';
  if (action.includes('bulk')) return 'text-amber-400';
  if (action.includes('purge')) return 'text-cyan-300';
  return 'text-indigo-300';
};

const ACTION_OPTIONS = [
  { id: 'all', label: 'All actions' },
  { id: 'user.update', label: 'User update' },
  { id: 'user.delete', label: 'User delete' },
  { id: 'user.bulk_update', label: 'Bulk update' },
  { id: 'user.bulk_delete', label: 'Bulk delete' },
  { id: 'invite.create', label: 'Invite create' },
  { id: 'invite.revoke', label: 'Invite revoke' },
  { id: 'storage.purge_orphans', label: 'Orphan purge' },
];

export const AdminAuditLog = ({ data, onChangeParams, params }) => {
  const [actorInput, setActorInput] = useState(params?.actor || '');
  const items = data?.items || [];
  const total = data?.total || 0;
  const offset = params?.offset || 0;
  const limit = params?.limit || 25;
  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const apply = (patch) => onChangeParams({ ...params, ...patch, offset: 0 });
  const setActor = () => apply({ actor: actorInput.trim() || undefined });

  const onReset = () => {
    setActorInput('');
    onChangeParams({ limit, offset: 0 });
  };

  return (
    <Card className="bg-zinc-950/60 border-white/5">
      <CardHeader className="flex flex-col gap-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <History className="w-4 h-4 text-muted-foreground" />
            Audit Log
            <span className="text-sm text-muted-foreground font-normal">({total})</span>
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="text-muted-foreground"
            data-testid="admin-audit-reset"
          >
            <FilterX className="w-3.5 h-3.5 mr-1.5" /> Reset filters
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={params?.action || 'all'}
            onValueChange={(v) => apply({ action: v === 'all' ? undefined : v })}
          >
            <SelectTrigger className="w-44 bg-zinc-900/60 border-white/10" data-testid="admin-audit-action-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTION_OPTIONS.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Input
              placeholder="Filter by actor email…"
              value={actorInput}
              onChange={(e) => setActorInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setActor()}
              onBlur={setActor}
              className="w-60 bg-zinc-900/60 border-white/10"
              data-testid="admin-audit-actor-filter"
            />
          </div>
          <Input
            type="date"
            value={params?.since?.slice(0, 10) || ''}
            onChange={(e) => apply({ since: e.target.value ? `${e.target.value}T00:00:00` : undefined })}
            className="w-44 bg-zinc-900/60 border-white/10"
            data-testid="admin-audit-since-filter"
          />
          <Input
            type="date"
            value={params?.until?.slice(0, 10) || ''}
            onChange={(e) => apply({ until: e.target.value ? `${e.target.value}T23:59:59` : undefined })}
            className="w-44 bg-zinc-900/60 border-white/10"
            data-testid="admin-audit-until-filter"
          />
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No matching audit entries.</p>
        ) : (
          <div className="space-y-1.5" data-testid="admin-audit-log">
            {items.map(e => (
              <div
                key={e.id}
                className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900/40 border border-white/5"
                data-testid={`admin-audit-row-${e.id}`}
              >
                <span className={cn('font-mono text-xs px-2 py-0.5 rounded-md bg-white/5 whitespace-nowrap', colorForAction(e.action))}>
                  {e.action}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    <span className="font-medium">{e.actor_name}</span>
                    <span className="text-muted-foreground"> → </span>
                    <span className="text-foreground">{e.target_label || e.target_id}</span>
                  </p>
                  {e.changes && Object.keys(e.changes).length > 0 && (
                    <p className="text-xs text-muted-foreground mt-0.5 font-mono break-all">
                      {Object.entries(e.changes).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')}
                    </p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap" title={new Date(e.created_at).toLocaleString()}>
                  {formatRelativeTime(e.created_at)}
                </span>
              </div>
            ))}
          </div>
        )}
        {total > limit && (
          <div className="flex items-center justify-between pt-4 mt-2 border-t border-white/5">
            <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={offset === 0}
                onClick={() => onChangeParams({ ...params, offset: Math.max(0, offset - limit) })}
                data-testid="admin-audit-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!data?.has_more}
                onClick={() => onChangeParams({ ...params, offset: offset + limit })}
                data-testid="admin-audit-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
